import {
  ConnectionPositionPair,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import {
  ElementRef,
  Injectable,
  InjectionToken,
  Injector,
} from '@angular/core';

export const BRN_OVERLAY_DATA = new InjectionToken('BrnOverlayData');
export const BRN_OVERLAY_REF = new InjectionToken('BrnOverlayRef');

// ===============tmp===============
export class BrnOverlayConfig {
  data?: any | null;
  position?: 'right' | 'bottom' | 'left' | 'top' | 'right-center';
  offsetX?: number;
  offsetY?: number;
  class?: string;
}

// ---------------------------------

@Injectable({
  providedIn: 'root',
})
export class BrnOverlayService {
  private overlayRefs: OverlayRef[] = [];

  private overlayRef!: OverlayRef;

  constructor(private overlay: Overlay, private injector: Injector) {}

  createOverlayPopup(
    component: ComponentType<any>,
    targetElement: ElementRef,
    config?: BrnOverlayConfig
  ): OverlayRef {
    const scrollStrategy = this.overlay.scrollStrategies.block();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(targetElement)
      .withPositions(
        this.getPositions(
          targetElement,
          config?.position || 'right',
          config?.offsetX,
          config?.offsetY
        )
      )
      .withViewportMargin(12)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'overlay-popup-backdrop',
      disposeOnNavigation: true,
      panelClass: config?.class,
    });

    const injector = this.createInjector(config);
    const portal = new ComponentPortal(component, null, injector);

    this.overlayRef.attach(portal);

    this.overlayRefs.push(this.overlayRef);

    this.overlayRef.backdropClick().subscribe(() => {
      this.close();
    });

    this.overlayRef.keydownEvents().subscribe((event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    return this.overlayRef;
  }

  createInjector(config?: BrnOverlayConfig): Injector {
    const userInjector = this.injector;
    const providers = [
      { provide: BRN_OVERLAY_DATA, useValue: config?.data },
      { provide: BRN_OVERLAY_REF, useValue: this.overlayRef },
    ];
    return Injector.create({ parent: userInjector, providers });
  }

  close(): void {
    this.overlayRef.detach();
    this.overlayRef.dispose();
  }

  closeAll(): void {
    this.overlayRefs.forEach((ref) => {
      ref.detach();
      ref.dispose();
    });
    this.overlayRefs = [];
  }

  hide(): void {
    this.overlayRef.addPanelClass('hidden');
  }

  show(): void {
    this.overlayRef.removePanelClass('hidden');
  }

  private getPositions(
    el: ElementRef,
    position: string,
    offsetX: number = 0,
    offsetY: number = 0
  ): ConnectionPositionPair[] {
    switch (position) {
      case 'right':
        return [
          new ConnectionPositionPair(
            { originX: 'end', originY: 'center' },
            { overlayX: 'start', overlayY: 'top' },
            16 + offsetX,
            -(el.nativeElement.offsetHeight / 2 + 14) + offsetY,
            ['pan--top-left']
          ),
          new ConnectionPositionPair(
            { originX: 'end', originY: 'center' },
            { overlayX: 'start', overlayY: 'bottom' },
            16 + offsetX,
            el.nativeElement.offsetHeight / 2 + 14 - offsetY,
            ['pan--bottom-left']
          ),
        ];

      case 'left':
        return [
          new ConnectionPositionPair(
            { originX: 'start', originY: 'center' },
            { overlayX: 'end', overlayY: 'top' },
            -18 + offsetX,
            -(el.nativeElement.offsetHeight / 2 + 14) + offsetY,
            ['pan--top-right']
          ),
          new ConnectionPositionPair(
            { originX: 'start', originY: 'center' },
            { overlayX: 'end', overlayY: 'bottom' },
            -18 + offsetX,
            el.nativeElement.offsetHeight / 2 + 14 - offsetY,
            ['pan--bottom-right']
          ),
        ];

      case 'bottom':
        return [
          new ConnectionPositionPair(
            { originX: 'start', originY: 'bottom' },
            { overlayX: 'start', overlayY: 'top' },
            -9 + offsetX,
            6 + offsetY,
            ['pan--bottom-left-start']
          ),
        ];
      case 'top':
        return [
          new ConnectionPositionPair(
            { originX: 'center', originY: 'top' },
            { overlayX: 'center', overlayY: 'bottom' },
            0 + offsetX,
            -6 + offsetY,
            'pan--bottom-center'
          ),
        ];
      case 'right-center':
        return [
          new ConnectionPositionPair(
            { originX: 'end', originY: 'center' },
            { overlayX: 'start', overlayY: 'center' },
            6 + offsetX,
            offsetY,
            ['popup-pointer--left-center']
          ),
        ];

      default:
        return [];
    }
  }
}
